// item 654 — the ONE place RNWY Statement-of-Work scope text is composed. Pure + deterministic
// (no React, no side effects) so it's unit-testable and can be diffed for the "auto-build until the
// user edits" logic in RnwyScopeManager. Composes the whole SOW from the venue model + CFG:
//   System scope (included/excluded) → High-Availability → each venue → SwitchPilot.
// The per-section builders are exported for tests; the app only calls composeFullScope.

import {
  parseVenues, RnwyVenue, RnwySwitchPilotInputs, RnwyJobLevel, hardwareTierLabel,
} from './rnwyVenues';
import {
  RnwyCfg, RnwySystemSize, RnwyCatalogItem, RnwyCategory,
} from './rnwyCfg';

// item 1186 (Chad) — "Other: <text>" is STORAGE grammar (item 1041); the customer-facing
// scope prints just the text the user typed.
const cleanOther = (x: string): string => (x.startsWith('Other: ') ? x.slice(7) : x);

// item 639/654 — the venue catalog grouped for the form AND the generated scope. Keys mirror the
// seeded CFG catalog (basix.da RnwyCfg.DefaultCatalog); custom keys fall into a trailing group.
// dart (2026-07-16, Chad) — each group carries a STABLE key. Categories are editable data keyed by
// this key; a rename changes only the display name, never the key, so items (which reference the key)
// are never touched by a rename. `keys` are the seeded catalog item ids that default into this group.
export const CATALOG_GROUPS: { key: string; title: string; keys: string[] }[] = [
  { key: 'jobwide', title: 'Job-wide', keys: ['ha_node', 'custom_branding'] },
  { key: 'video', title: 'Video distribution', keys: ['decoder_drop', 'tiling', 'encoder_noncontrol', 'encoder_control'] },
  { key: 'audio', title: 'Audio', keys: ['audio_zone', 'audio_mixer', 'audio_enc_noncontrol', 'audio_enc_control', 'room_combine', 'room_combine_room'] },
  { key: 'lighting', title: 'Lighting', keys: ['lighting_preset', 'lighting_load'] },
  { key: 'ui', title: 'User interface', keys: ['touch_panel_sm', 'touch_panel_lg', 'runway_provision'] },
  { key: 'videowalls', title: 'Video walls', keys: ['videowall_framework', 'videowall_custom'] },
  { key: 'accessories', title: 'Accessories', keys: ['projector_screen', 'projector_lift', 'tv_lift', 'surround_receiver', 'doorbell'] },
  { key: 'shades_hvac', title: 'Shades / HVAC', keys: ['drape_motor', 'shade_motor', 'hvac_zone'] },
  { key: 'illustrations', title: 'Illustrations', keys: ['illus_40k', 'illus_30k', 'illus_20k', 'illus_10k', 'illus_room_combine'] },
];

// item 654 — HA nodes are PROPOSAL-level (entered once, priced on venue 1) — never a per-venue
// quantity. Excluded from the per-venue grid + the per-venue scope.
export const PROPOSAL_LEVEL_KEYS = new Set(['ha_node']);

// the trailing bucket for anything not in a named category.
export const UNCATEGORIZED_KEY = '__additional';
export const UNCATEGORIZED = 'Additional systems'; // display name for the uncategorized bucket

// item id -> default category KEY (seeded items that carry no explicit categoryKey)
const KEY_TO_CATKEY: Record<string, string> = {};
CATALOG_GROUPS.forEach((g) => g.keys.forEach((k) => { KEY_TO_CATKEY[k] = g.key; }));

// item 654 — marketing lead-in per category so the SOW reads like a designed proposal. Keyed by the
// STABLE category key, so a rename keeps the copy.
export const CATEGORY_COPY: Record<string, string> = {
  jobwide: 'System-wide foundation shared across the entire deployment.',
  // item 1425 (Chad: "No follow up text after either audio or video distro") — Video distribution
  // and Audio print as bare headings; no lead-in copy.
  lighting: 'Scene-based, one-touch lighting woven directly into the RNWY experience.',
  ui: 'Intuitive control surfaces the team will actually enjoy using every day.',
  videowalls: 'Bespoke, edge-to-edge video canvases built for the room’s signature moments.',
  accessories: 'Motorized and AV accessories — the finishing hardware that makes it feel effortless.',
  shades_hvac: 'Daylight and climate, automated and tied into every scene.',
  illustrations: 'Architectural design illustrations so the client sees the finished space before work begins.', // item 1189 (Chad) — software co: no photoreal, no cable-pulling
};

// the seeded categories (stable keys + default names) — used when a CFG predates editable categories.
export const DEFAULT_CATEGORIES: RnwyCategory[] = CATALOG_GROUPS.map((g) => ({ key: g.key, name: g.title, brands: [] }));

// an item's stable category KEY (never changes on rename). Precedence: explicit categoryKey that still
// exists → legacy `category` NAME matched to a category → the seeded item-key map → uncategorized. The
// name-match + key-map fallbacks recover any item stranded by the old name-cascade rename.
export const categoryKeyOf = (
  item: { key: string; categoryKey?: string; category?: string },
  categories: RnwyCategory[],
): string => {
  if (item.categoryKey && categories.some((c) => c.key === item.categoryKey)) return item.categoryKey;
  if (item.category && item.category.trim()) {
    const byName = categories.find((c) => c.name === item.category);
    if (byName) return byName.key;
  }
  return KEY_TO_CATKEY[item.key] || UNCATEGORIZED_KEY;
};

// give every category a stable key. Categories saved BEFORE keys existed have none; they were seeded
// from DEFAULT_CATEGORIES in order and never reordered, so assign the default key BY POSITION (this
// preserves the jobwide/video/… association through any rename). Extras beyond the defaults get a
// derived key. Idempotent — a category that already has a key keeps it.
export const normalizedCategories = (cfg: RnwyCfg | null): RnwyCategory[] => {
  const base = cfg?.categories && cfg.categories.length ? cfg.categories : DEFAULT_CATEGORIES;
  const withKeys = base.map((c, i) => ({
    ...c,
    key: c.key || (i < DEFAULT_CATEGORIES.length ? DEFAULT_CATEGORIES[i].key : `cat_${i}`),
  }));
  // ensure the fallback bucket exists as a REAL, editable category (Chad: "Additional systems" should
  // be editable). Its key stays UNCATEGORIZED_KEY so items with no category still land in it.
  if (!withKeys.some((c) => c.key === UNCATEGORIZED_KEY)) {
    withKeys.push({ key: UNCATEGORIZED_KEY, name: UNCATEGORIZED, brands: [] });
  }
  return withKeys;
};

// the categories to render, in order — now identical to normalizedCategories (which includes the
// editable uncategorized bucket). Every item resolves into one of these via categoryKeyOf.
export const orderedCategories = (cfg: RnwyCfg | null): RnwyCategory[] => normalizedCategories(cfg);

// dart (Chad, 2026-07-15) — the module ids in reading order + their DEFAULT display names. The names
// are now editable per company (CFG.moduleLabels, mirrored in basix.da RnwyCfg.ModuleLabels); a blank
// or missing override falls back to the default here. Keep the ORDER fixed (don't iterate the labels
// object) so the scope reads the same regardless of how the override object was keyed.
export const MODULE_ORDER = ['concourse', 'aviator', 'switchPilot', 'thirdParty'] as const;
export const DEFAULT_MODULE_LABELS: Record<string, string> = {
  concourse: 'Concourse®', aviator: 'Aviator™', switchPilot: 'SwitchPilot®', thirdParty: 'Third-party integration',
};

// System scope — Included / Excluded, from the module toggles + which venue categories are used.
export const systemScopeBlock = (
  modules: Record<string, boolean>,
  venues: RnwyVenue[],
  moduleLabels?: Record<string, string>,
): string => {
  // effective name = the per-company override (when non-blank) else the default
  const nameFor = (k: string): string => (moduleLabels?.[k]?.trim() || DEFAULT_MODULE_LABELS[k]);
  const inMods = MODULE_ORDER.filter((k) => modules[k]).map((k) => nameFor(k));
  const exMods = MODULE_ORDER.filter((k) => !modules[k]).map((k) => nameFor(k));
  const inCats = CATALOG_GROUPS
    .filter((g) => venues.some((v) => g.keys.some((key) => (v.quantities[key] || 0) > 0)))
    .map((g) => g.title);
  const includedBits = [inMods.join(', '), inCats.join(', ')].filter(Boolean).join(' — ');
  return [
    '## System scope',
    '',
    `**Included:** ${includedBits || 'To be confirmed'}. All features supported by these systems are included.`,
    '',
    `**Excluded:** ${exMods.length ? `${exMods.join(', ')} — ` : ''}and all other subsystems not listed above.`,
  ].join('\n');
};

export const haScopeBlock = (haNodes: number): string => (
  `## High-Availability\n- ${haNodes} redundant node${haNodes === 1 ? '' : 's'} for automatic failover — no single point of failure`
);

export const venueScopeBlock = (v: RnwyVenue, sizes: RnwySystemSize[], catalog: RnwyCatalogItem[], cats: RnwyCategory[] = orderedCategories(null), propEquip: Record<string, string[]> = {}, propSources: string[] = []): string => {
  const size = sizes.find((s) => s.key === v.systemSizeKey)?.label ?? v.systemSizeKey;
  const bullet = (key: string, label: string): string | null => {
    const q = v.quantities[key] || 0;
    // item 1185 (Chad) — per-line notes print as an explicit annotation: "(4) Audio Sources — [NOTE: RockBot BGM Player]"
    return q > 0 ? `- ${q} × ${label}${v.notes?.[key] ? ` — [NOTE: ${v.notes[key]}]` : ''}` : null;
  };
  const out: string[] = [
    `## ${v.name || 'Venue'} — ${size} system`,
    `A fully integrated RNWY control experience engineered for this space on a ${String(size).toLowerCase()} system footprint.`,
  ];
  // group by each item's stable category KEY, in the tenant's category order (`cats`), then the
  // trailing "Additional systems" bucket. Within a category, catalog array order wins — so a
  // drag-reorder in the CFG editor shows through to the scope.
  cats.forEach((cat) => {
    const rows = catalog
      .filter((c) => categoryKeyOf(c, cats) === cat.key && !PROPOSAL_LEVEL_KEYS.has(c.key)) // HA is proposal-level
      .map((c) => bullet(c.key, c.label))
      .filter((r): r is string => r !== null);
    if (!rows.length) return;
    const copy = CATEGORY_COPY[cat.key];
    out.push(`\n**${cat.name}**${copy ? ` — ${copy}` : ''}`, ...rows);
  });
  // item 681(E)/684 (Chad) — the RNWY-controlled equipment this venue selected (from the CFG list),
  // auto-written into scope, grouped by category. De-duped against the property-wide picks so shared
  // gear isn't listed twice (it already prints once in the Property-wide block).
  const equip = v.controlledEquipment ?? {};
  const equipRows = Object.keys(equip)
    .map((catName) => {
      const items = (equip[catName] ?? []).filter((x) => (x ?? '').trim() && !(propEquip[catName] ?? []).includes(x));
      return items.length ? `- ${catName}: ${items.map(cleanOther).join(', ')}` : null;
    })
    .filter((r): r is string => r !== null);
  if (equipRows.length) out.push('\n**Equipment we control with RNWY**', ...equipRows);
  // item 681/684 (Chad) — the individual controllable SOURCES this venue includes, de-duped against
  // the property-wide sources.
  const sources = (v.sources ?? []).filter((x) => (x ?? '').trim() && !propSources.includes(x));
  if (sources.length) out.push('\n**Sources included**', ...sources.map((s) => `- ${cleanOther(s)}`));
  return out.join('\n');
};

export const switchPilotScopeBlock = (sp: RnwySwitchPilotInputs): string => {
  const lines: string[] = [
    '## SwitchPilot — intelligent network switching',
    'Fully RNWY-managed network switching. SwitchPilot configures and maintains your VLANs and '
      + 'VLAN tagging, enables and disables ports on demand, monitors live port activity, and manages '
      + 'Power-over-Ethernet — delivering PoE and switching it on or off per port — so the network '
      + 'stays organized, observable, and under control.',
    // item 1399 (Chad) — the default SwitchPilot verbiage states who owns the switch configuration:
    // Pulse Group hands off and trains (one-on-one, VIRTUALLY — item 1412: never "white glove", Pulse
    // is never on site); the configuration files and every port decision
    // belong to the customer and/or their integrator, and Pulse carries no responsibility for a wrong
    // or invalid configuration.
    'Switch configuration files are created, set, and loaded into the designated folder by your team '
      + 'and/or your integrator — not by Pulse Group. Pulse Group provides a personalized, one-on-one '
      + 'hand-off and training session, delivered virtually, on how to do it. Enabling, disabling, or changing any port or VLAN is your decision and '
      + 'your responsibility; Pulse Group carries no responsibility for wrong or invalid configurations that '
      + 'take down a network or cause issues.',
  ];
  const n = sp.switchCount;
  if (n > 0) {
    // clean phrasing: "{n} managed {brand} switch(es)" — SwitchPilot manages the supported brand's
    // switches (we don't manufacture them), so the brand stands alone.
    lines.push(`- ${n} managed ${sp.brand ? `${sp.brand} ` : ''}switch${n === 1 ? '' : 'es'}`);
  }
  if (sp.specialItems?.length) {
    sp.specialItems.forEach((it) => { if (it.label) lines.push(`- ${it.label}`); });
  } else if (sp.specialFeatures > 0) {
    lines.push('- Special-feature configuration (per quote)');
  }
  return lines.join('\n');
};

// item 684 (Chad) — the PROPERTY-WIDE equipment/sources (entered once at the job level, shared across
// every venue). Emitted ONCE as its own block so shared gear isn't repeated on every venue.
export const propertyScopeBlock = (jl: RnwyJobLevel): string => {
  const equip = jl.controlledEquipment ?? {};
  const equipRows = Object.keys(equip)
    .map((catName) => {
      const items = (equip[catName] ?? []).filter((x) => (x ?? '').trim());
      return items.length ? `- ${catName}: ${items.map(cleanOther).join(', ')}` : null;
    })
    .filter((r): r is string => r !== null);
  const sources = (jl.sources ?? []).filter((x) => (x ?? '').trim());
  const ha = jl.haNodes > 0;
  // item 1040 — the portal's Add-hardware pick rides the property-wide block; qty > 1 IS HA
  const hw = jl.hardware && jl.hardware.sku && jl.hardware.qty > 0 ? jl.hardware : null;
  const ofp = !hw && !!jl.ownerFurnishedProcessor; // item 1483
  if (!equipRows.length && !sources.length && !ha && !hw && !ofp) return '';
  const out: string[] = ['## Property-wide', 'Shared across all venues on this property.'];
  if (equipRows.length) out.push('\n**Equipment we control with RNWY**', ...equipRows);
  if (sources.length) out.push('\n**Sources included**', ...sources.map((s) => `- ${cleanOther(s)}`));
  if (hw) out.push('\n**RNWY hardware**', `- ${hw.qty} × ${hardwareTierLabel(hw.sku)}${hw.qty > 1 ? ' — high availability (automatic)' : ''}`);
  if (ofp) out.push('\n**RNWY hardware**', '- Owner-furnished processor — provided by owner, imaged by RNWY');
  // item 685 (Chad) — High-Availability is property-wide too; render it as a black **sub-heading** here
  // (matching Equipment/Sources) instead of its own purple ## block.
  if (ha) out.push('\n**High-Availability**', `- ${jl.haNodes} redundant node${jl.haNodes === 1 ? '' : 's'} for automatic failover — no single point of failure`);
  return out.join('\n');
};

// Compose the FULL Statement of Work from every input, in reading order. Deterministic: the same
// model + cfg always produce the same string (that's what lets RnwyScopeManager tell "the user
// edited this" from "nothing changed").
export const composeFullScope = (venuesJson: string | null | undefined, cfg: RnwyCfg | null): string => {
  const model = parseVenues(venuesJson ?? undefined);
  const jl = model.jobLevel;
  const sizes = cfg?.systemSizes ?? [];
  const catalog = cfg?.catalog ?? [];
  const sp = jl.switchPilot;
  const cats = orderedCategories(cfg); // tenant category order + names (renames + customs)
  const blocks: string[] = [];
  blocks.push(systemScopeBlock(jl.modules as unknown as Record<string, boolean>, model.venues, cfg?.moduleLabels));
  // item 684/685 — property-wide shared equipment/sources + High-Availability, emitted once
  const propBlock = propertyScopeBlock(jl);
  if (propBlock) blocks.push(propBlock);
  // item 684 — pass the property-wide picks so each venue de-dupes against them (no double-listing)
  model.venues.forEach((v) => blocks.push(venueScopeBlock(v, sizes, catalog, cats, jl.controlledEquipment ?? {}, jl.sources ?? [])));
  const spHasContent = sp.switchCount > 0 || (sp.specialItems?.length ?? 0) > 0 || sp.specialFeatures > 0;
  if (jl.modules.switchPilot && spHasContent) blocks.push(switchPilotScopeBlock(sp));
  return blocks.join('\n\n');
};
